
import { HangingIncoming, FollowUpGroup, AWB, FollowUpSTT } from '@/domain/entities/HangingIncoming';
import { Options, Vue, prop } from 'vue-class-component';
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Checkbox from "primevue/checkbox";
import whatsapp from '@/app/infrastructures/misc/common-library/Whatsapp';
import formatDate from '@/app/infrastructures/misc/common-library/v2/FormatDate';
import { HangingLastMileConfirmApiRequest } from '@/data/payload/api/HangingApiRequest';
import ConfirmAction from "../../components/ConfirmAction.vue";
import { FollowUpGroupLastMile, GroupSttsLastMile, HangingLastMile } from '@/domain/entities/HangingLastMile';
import { MainAppController } from '@/app/ui/controllers/MainAppController';
import { SttHangingController } from '@/app/ui/controllers/SttHangingController';
import { AccountController } from "@/app/ui/controllers/AccountController";

class Props {
  modelValue = prop<HangingLastMile[]>({
    default: () => [],
    required: true
  });
  loading = prop<boolean>({
    default: false,
    type: Boolean
  });
  skeletonCount = prop<number>({
    default: 3,
    type: Number
  });
}

@Options({
  components: {
    Accordion,
    AccordionTab,
    Checkbox,
    ConfirmAction
  },
  emits: ["update:modelValue", "template", "confirm", "download"]
})
class CardCollapsible extends Vue.with(Props) {
  isOpenModal = false;
  followUpData = new HangingLastMileConfirmApiRequest();
  lastConfirmData = new HangingLastMile();

  mapActivityType(type: HangingLastMile) {
    const label: any = {
      "follow_up_courier": `${this.$t("dashboard.hanging.followUpCourier")} ${type.followUp?.courierName}`,
      "follow_up_pic_last_mile": this.$t("dashboard.hanging.followUpPicLastMile"),
      "follow_up_warehouse": this.$t("dashboard.hanging.searchSttWarehouse")
    }

    const icon: any = {
      "follow_up_courier": "ic_truck_hanging",
      "follow_up_pic_last_mile": "ic_pic_hanging",
      "follow_up_warehouse": "ic_search_hanging",
    }

    return {
      label: label[type.activityType] ?? this.$t("dashboard.hanging.followUpCourier"),
      icon: icon[type.activityType] ?? "ic_plane_hanging"
    }
  }

  mapFollowUp(type: HangingLastMile) {
    const label: any = {
      "belum": this.$t("dashboard.hanging.notFollowedUp"),
      "sudah": this.$t("dashboard.hanging.followUpDone")
    }

    const icon: any = {
      "belum": "warning-red",
      "sudah": "check-circle"
    }

    const color: any = {
      "belum": "text-red-lp-2000",
      "sudah": "text-green-lp-100"
    }

    return {
      label: label[type.followUpStatus] ?? this.$t("dashboard.hanging.notFollowedUp"),
      icon: icon[type.followUpStatus] ?? "warning-red",
      color: color[type.followUpStatus] ?? "text-red-lp-2000"
    }
  }

  mapHangingDays(stt: GroupSttsLastMile) {
    const colorMap: Record<number, string> = {
      1: "bg-aqua-lp-200 text-aqua-lp-500",
      2: "bg-aqua-lp-200 text-aqua-lp-500",
      3: "bg-aqua-lp-200 text-aqua-lp-500",
      4: "bg-aqua-lp-200 text-aqua-lp-500",
      5: "bg-yellow-lp-200 text-yellow-lp-100",
      6: "bg-orange-lp-100 text-orange-lp-200"
    };

    let color = "bg-white text-gray-lp-300";

    if (stt.hangingDays != null) {
      if (colorMap[stt.hangingDays]) {
        color = colorMap[stt.hangingDays];
      } else if (stt.hangingDays >= 7) {
        color = "bg-red-lp-2000 text-white";
      }
    }

    const label =
      stt.hangingDays != null
        ? stt.hangingDays < 7
          ? `${stt.hangingDays} ${this.$t('dashboard.hanging.dayUnit')}`
          : this.$t('dashboard.hanging.day7Plus')
        : "-";

    return { label, color };
  }

  mapTicketStatus(stt: GroupSttsLastMile) {
    const label: any = {
      "belum_ditemukan": this.$t('dashboard.hanging.statusNotFound'),
      "ditemukan_konsol": this.$t('dashboard.hanging.statusFoundConsole'),
      "ditemukan_pos": this.$t('dashboard.hanging.statusFoundPos')
    }

    const color: any = {
      "belum_ditemukan": "bg-pink-lp-200 text-red-lp-100",
      "ditemukan_konsol": "bg-blue-lp-600 text-blue-lp-700",
      "ditemukan_pos": "bg-purple-lp-100 text-purple-lp-800"
    }

    return {
      label: label[stt.existenceStatus] ?? "-",
      color: color[stt.existenceStatus] ?? "",
      isConsole: stt.existenceStatus === "ditemukan_konsol",
      isPos: stt.existenceStatus === "ditemukan_pos"
    }
  }

  isFollowUpDone(type: HangingLastMile) {
    return type.followUpStatus === 'sudah';
  }

  onClickTemplate(data: HangingLastMile) {
    this.$emit("template", data)
  }

  onConfirmDone(data: HangingLastMile) {
    this.lastConfirmData = data;
    this.followUpData = new HangingLastMileConfirmApiRequest({
      activityType: data.activityType,
      courierName: data.followUp?.courierName,
      courierPhone: data.followUp?.courierPhone
    })
    this.isOpenModal = true
  }

  onClickWhatsapp(item: HangingLastMile) {
    if(!item.followUp) return
    const cityCode = this.accountData.account_type_detail.partnerLocation?.city?.code;
    const group = item.groups;
    const groupName = group.flatMap(item => item.groupName).join(", ");
    const stt = group.flatMap((group) => group.stts);
    const sttList = stt
      .map((sttItem) => `${sttItem.sttNo}`)
      .join('\n');

    const templateMessage =
      `${this.$t("dashboard.hanging.templateIntro")} ${this.$t("dashboard.hanging.accountTypeKonsolidator")} ${this.$t("dashboard.hanging.templateLionParcel")} ${cityCode}.\n` +
      `${this.$t("dashboard.hanging.templateHanging")} (${groupName}) ${this.$t("dashboard.hanging.templateMoreThan3Days")}.\n` +
      `${this.$t("dashboard.hanging.templateTotal", { count: stt.length })}\n` +
      `${sttList}\n` +
      `${this.$t("dashboard.hanging.templateHelpQuestion")} ${this.$t("dashboard.hanging.templateThanks")}.`;
    whatsapp(item.followUp?.courierPhone, templateMessage);
  }

  get accountData() {
    return AccountController.accountData;
  }

  onDownload(item: HangingLastMile) {
    const listStt = item.groups?.flatMap(group => group?.stts).map(stt => stt?.sttNo);
    this.$emit("download", listStt)
  }

  onSearch() {
    this.$router.push('/cari-stt/scan')
  }

  onTabOpen(item: HangingLastMile) {
    item.isCollapsed = true;
    this.$emit("update:modelValue", this.modelValue);
  }

  onTabClose(item: HangingLastMile) {
    item.isCollapsed = false;
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateGroup(item: FollowUpGroupLastMile) {
    item.isChecked = !item.isChecked;
    item.stts.forEach(stt => {
      stt.isChecked = item.isChecked;
    })
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateStts(stts: FollowUpSTT[], group: FollowUpGroupLastMile) {
    group.isChecked = !group.isChecked;
    this.$emit("update:modelValue", this.modelValue);
  }

  onUpdateStt(stt: FollowUpSTT, group: FollowUpGroupLastMile) {
    stt.isChecked = !stt.isChecked;
    group.isChecked = group?.stts?.every((sttItem) => sttItem.isChecked) ?? false;
    this.$emit("update:modelValue", this.modelValue);
  }

  // Format Date
  formatDateToString(date: string) {
    return formatDate(date);
  }

  async onDone() {
    try {
      this.isOpenModal = false;
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();

      await SttHangingController.hangingLastMileConfirmFollowUp(
        this.followUpData
      );


      MainAppController.setSnackbarMessage(this.$t("dashboard.hanging.successUpdate"));
      MainAppController.setSnackbarTimeout(5000);
      MainAppController.setSnackbar(true);

      this.$emit("success-done")
    } catch (err) {
      MainAppController.setSnackbarMessage(this.$t("dashboard.hanging.failUpdate"));
      MainAppController.setSnackbarTimeout(5000);
      MainAppController.setSnackbar(true);
      MainAppController.setSnackbarPositivity(false);
    } finally {
      MainAppController.closeLoading();
      this.followUpData = new HangingLastMileConfirmApiRequest();
    }
  }

  onFailedConfirmDone() {
    this.onConfirmDone(this.lastConfirmData);
  }

  onSuccessConfirmDone() {
    this.$emit("confirm");
  }

  disableButton(item: HangingLastMile) {
    if (!item.groups) return false;
    const stts = item.groups?.flatMap(group => group.stts)
    const noneChecked = stts.every(stt => !stt?.existenceStatus)

    return noneChecked
  }

}
export default CardCollapsible;
